/**
 * File: DateRangeSelect.tsx
 * Path: apps/admin/src/shared/components/overview/DateRangeSelect.tsx
 * Description: The Overview's date range: one button showing the real dates,
 * opening quick ranges on the left and a month calendar on the right, with
 * From/To boxes, a note on the comparison dates, and Cancel / Apply. The
 * calendar is square and starts on Monday; the first tap sets the start, the
 * second sets the end.
 */

import React from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@idk/ui";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { cn } from "@idk/utils";
import { fieldClass, labelClass } from "@/shared/components/LeadPrimitive";
import { PRESETS, shortDate, type DateRange, type PresetId } from "../../utils/overviewFiguresUtils";
import { MONTH_NAMES, WEEKDAYS, weeksOf } from "../../utils/dateRangeUtils";
import { useDateRangeSelectController } from "../../hooks/useDateRangeSelectController";

interface Props {
  range: DateRange;
  today: string;
  booksStart: string;
  yearEndMonth: number;
  presetRange: (id: PresetId) => DateRange;
  onChange: (range: DateRange) => void;
  /** Open from outside (the Reports "A period I pick" step). Leave out for the usual self-opening button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Words before the dates on the button, and the heading over the calendar, when this picks something other than the page's range. */
  label?: string;
  /** Leave out the note about last year's dates (it has no meaning when picking a comparison). */
  plain?: boolean;
}

const GROUPS: PresetId[][] = [
  ["thisYear", "lastYear"],
  ["thisMonth", "lastMonth", "thisQuarter", "last12"],
  ["allTime"],
];

export const DateRangeSelect: React.FC<Props> = ({
  range,
  today,
  booksStart,
  yearEndMonth,
  presetRange,
  onChange,
  open: controlledOpen,
  onOpenChange,
  label,
  plain = false,
}) => {
  const c = useDateRangeSelectController({ range, today, booksStart, presetRange, onChange, open: controlledOpen, onOpenChange });
  const { open, draft, month, activePreset, problem, comparison } = c;

  return (
    <Popover
      open={open}
      onOpenChange={c.onOpenChange}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-11 w-full justify-between rounded-none sm:w-auto" aria-label={`${label ?? "Date range"}: ${shortDate(range.from)} to ${shortDate(range.to)}`}>
          <CalendarDays className="mr-stack-xs h-4 w-4" aria-hidden />
          {label && <span className="mr-stack-xs font-normal text-muted-foreground">{label}:</span>}
          <span className="tabular-nums">
            {shortDate(range.from)} – {shortDate(range.to)}
          </span>
          <ChevronDown className="ml-stack-xs h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={label ? "start" : "end"} collisionPadding={16} className="max-h-screen w-screen max-w-prose-base overflow-y-auto rounded-none p-0">
        <div className="flex flex-col sm:flex-row">
          <div className="border-b border-border/60 sm:w-56 sm:shrink-0 sm:border-b-0 sm:border-r" role="group" aria-label="Quick ranges">
            {GROUPS.map((group, g) => (
              <ul key={g} className={cn("grid grid-cols-2 sm:grid-cols-1", g > 0 && "border-t border-border/60")}>
                {group.map((id) => {
                  const preset = PRESETS.find((p) => p.id === id);
                  if (!preset) return null;
                  const label = id === "allTime" ? `All time (since ${shortDate(booksStart)})` : preset.label;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        aria-pressed={activePreset === id}
                        onClick={() => c.choosePreset(id)}
                        className={cn(
                          "flex min-h-11 w-full items-center border-l-2 px-gutter-sm text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          activePreset === id ? "border-l-primary bg-primary/10 font-bold text-primary" : "border-l-transparent hover:bg-secondary",
                        )}
                      >
                        {label}
                      </button>
                    </li>
                  );
                })}
                {g === GROUPS.length - 1 && (
                  <li>
                    <button
                      type="button"
                      aria-pressed={activePreset === undefined}
                      onClick={c.chooseCustom}
                      className={cn(
                        "flex min-h-11 w-full items-center border-l-2 px-gutter-sm text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        activePreset === undefined ? "border-l-primary bg-primary/10 font-bold text-primary" : "border-l-transparent hover:bg-secondary",
                      )}
                    >
                      Custom range
                    </button>
                  </li>
                )}
              </ul>
            ))}
          </div>

          <div className="min-w-0 flex-1 space-y-stack-md p-gutter-md">
            {label && <p className="text-sm font-bold">{label}</p>}
            <div className="flex items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                disabled={!c.canGoBack}
                onClick={c.goBack}
                className="inline-flex h-11 w-11 items-center justify-center hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <p className="text-sm font-bold" aria-live="polite">
                {MONTH_NAMES[Number(month.slice(5, 7)) - 1]} {month.slice(0, 4)}
              </p>
              <button
                type="button"
                aria-label="Next month"
                disabled={!c.canGoForward}
                onClick={c.goForward}
                className="inline-flex h-11 w-11 items-center justify-center hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div role="grid" aria-label={`${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`} className="space-y-px">
              <div role="row" className="grid grid-cols-7">
                {WEEKDAYS.map((d, i) => (
                  <span key={i} role="columnheader" className="py-stack-xs text-center text-xs text-muted-foreground">
                    {d}
                  </span>
                ))}
              </div>
              {weeksOf(month).map((week, w) => (
                <div key={w} role="row" className="grid grid-cols-7 gap-px">
                  {week.map((day, i) => {
                    if (!day) return <span key={i} role="gridcell" />;
                    const unavailable = day > today || day < booksStart;
                    const edge = day === draft.from || day === draft.to;
                    const inside = day > draft.from && day < draft.to;
                    return (
                      <span key={day} role="gridcell" className="flex">
                        <button
                          type="button"
                          disabled={unavailable}
                          aria-pressed={edge}
                          aria-label={shortDate(day)}
                          onClick={() => c.pickDay(day)}
                          className={cn(
                            "h-11 w-full text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40",
                            edge ? "bg-primary font-bold text-primary-foreground" : inside ? "bg-primary/20 text-foreground" : "text-foreground hover:bg-secondary",
                          )}
                        >
                          {Number(day.slice(8, 10))}
                        </button>
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-stack-sm">
              <div className="space-y-stack-xs">
                <label htmlFor="range-from" className={labelClass}>
                  From
                </label>
                <input id="range-from" type="date" value={draft.from} min={booksStart} max={today} onChange={(e) => c.setField("from", e.target.value)} className={`${fieldClass} w-full`} />
              </div>
              <div className="space-y-stack-xs">
                <label htmlFor="range-to" className={labelClass}>
                  To
                </label>
                <input id="range-to" type="date" value={draft.to} min={booksStart} max={today} onChange={(e) => c.setField("to", e.target.value)} className={`${fieldClass} w-full`} />
              </div>
            </div>

            {problem ? (
              <p role="alert" className="text-sm text-tone-red">
                {problem}
              </p>
            ) : plain ? null : (
              <p className="text-sm text-muted-foreground">
                Financial year ends in {MONTH_NAMES[yearEndMonth - 1]}. Comparison: same dates last year ({comparison}).
              </p>
            )}

            <div className="flex justify-end gap-stack-sm">
              <Button type="button" variant="secondary" className="h-11 rounded-none" onClick={c.cancel}>
                Cancel
              </Button>
              <Button
                type="button"
                className="h-11 rounded-none"
                disabled={problem !== null}
                onClick={c.apply}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
